import type { ChatResponse } from '../gen/openapi/models/ChatResponse';

/** One tool call of an `mcp_call` turn, as the activity trail needs it. */
export type McpCallRecord = {
  id: string | number;
  name: string;
  explanation?: string;
  requiresApproval: boolean;
  /** The resources the call acts on (bucket names, object keys…), never its raw arguments. */
  resources: string[];
};

export enum ToolOutcome {
  Done = 'done',
  Failed = 'failed',
  /** The user denied this call. */
  Denied = 'denied',
  /** Never run: the user denied an earlier call of the same turn. */
  Skipped = 'skipped',
}

export type ToolOutcomeRecord = {
  outcome: ToolOutcome;
  /** The call ran because the user approved it, not because it needed no approval. */
  approvedByUser: boolean;
};

export enum ToolTrailStatus {
  AwaitingApproval = 'awaiting_approval',
  Running = 'running',
  Done = 'done',
  Failed = 'failed',
  Denied = 'denied',
  Skipped = 'skipped',
}

export type ToolTrailRow = {
  id: string | number;
  name: string;
  label: string;
  status: ToolTrailStatus;
  approvedByUser: boolean;
  isNavigation: boolean;
};

/** What the approval banner asks about: every awaiting call of the active call's tool. */
export type ToolApprovalRequest = {
  toolName: string;
  explanations: string[];
  resources: string[];
  callCount: number;
};

type ClientMessageBase = ChatResponse & {
  /**
   * Indicates whether this message represents a system error that occurred
   * during the chat interaction (e.g., network errors, API failures).
   * When true, the UI may display retry buttons or other error recovery options.
   */
  isError?: boolean;
  /**
   * Hidden from the chat feed but still sent to the agent. Used for programmatic
   * turns like the dashboard auto-summary, whose reply is the visible landing message.
   */
  hidden?: boolean;
};

/**
 * A stored chat message: what is persisted and sent to the agent. `mcpKind` marks the
 * entries of the MCP tool-call loop, which the feed hides — their payload is kept so the
 * LLM has continuity on subsequent turns — and decides which tool fields the entry carries.
 */
export type ClientChatResponse = ClientMessageBase &
  (
    | { mcpKind?: undefined }
    /** The turn's calls, in the order they run. */
    | { mcpKind: 'mcp_call'; mcpCalls?: McpCallRecord[] }
    /** How each call of the turn ended, keyed by JSON-RPC id. */
    | { mcpKind: 'mcp_response'; mcpOutcomes?: Record<string, ToolOutcomeRecord> }
    | { mcpKind: 'background_handoff' }
  );

/** A request's tool calls folded into one card of the feed; derived, never persisted. */
export type ToolTrailEntry = {
  kind: 'tool_trail';
  timestamp: Date;
  /** One row per tool reached so far. */
  rows: ToolTrailRow[];
  /** On a live trail: the approval the user is being asked for. */
  approval?: ToolApprovalRequest;
};

/** What the chat renders: stored messages and the tool trails built from them. */
export type ChatFeedEntry = ClientChatResponse | ToolTrailEntry;

export const isToolTrail = (entry: ChatFeedEntry | undefined): entry is ToolTrailEntry =>
  entry !== undefined && 'kind' in entry && entry.kind === 'tool_trail';
